<template>
  <v-container class="physical-activity-goal-setting-weekly-sports-goals">
    <v-form ref="form" v-model="valid" lazy-validation>
      <v-row>
        <v-col cols="12" lg="10" xl="8">
          <v-row class="mx-5 ma-2">
            <v-col cols="8">
              <h3>
                {{ $ucfirst($t("pa.weeklySportsGoal"))
                }}{{ " (" + $t("pa.kcal") + ")" }}
              </h3>
            </v-col>
            <v-col cols="4" align="end">
              <!-- Edit exercise prescription dialog -->
              <v-dialog v-model="showDialog" max-width="600px">
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-if="isExpertToolAvailable"
                    @click="editExpertTool"
                    v-on="on"
                  >
                    <v-icon> mdi-pencil-outline </v-icon>
                    {{ $ucfirst($t("pa.editSportsGoal")) }}
                  </v-btn>
                </template>
                <div class="dashboard-dialog-window">
                  <div class="dashboard-dialog-window__message">
                    {{ $ucfirst($t('pa.enterSubjectId')) }}
                  </div>
                  <v-text-field
                    class="mt-6"
                    v-model="patientStudyId"
                    dense
                    :error="!!studyIdError"
                    :error-messages="studyIdError"
                    :placeholder="$ucfirst($t('pdf.medicationPrescription.subjectId', {catalog: 'messages'}))"
                    ref="dialogInput"
                  >
                  </v-text-field>
                  <div class="dashboard-dialog-window__actions mt-2">
                    <v-btn
                      x-large
                      text
                      class="ml-4 mt-2"
                      @click="editExpertTool"
                    >
                      {{ $ucfirst($t('pa.editSportsGoal')) }}
                    </v-btn>
                    <v-btn
                      x-large
                      text
                      class="ml-4 mt-2"
                      @click="closeDialog"
                      color="primary"
                    >
                      {{ $ucfirst($t('buttons.cancel', {catalog: 'app_mobile'})) }}
                    </v-btn>
                  </div>
                </div>
              </v-dialog>
            </v-col>
          </v-row>
          <v-row class="mx-5 mt-2 mb-6">
            <p-a-weekly-sports-goal
              :flag="flag"
              :finish="finish"
              :progressNumber="progress"
              :activities="activities"
              :barWidth="550"
              @activitySelected="activityModification"
              :disabledProgress="true"
            />
          </v-row>
<!--          // Note: Release 2.0 scope reduction-->
<!--          <v-row class="mx-5 my-2">-->
<!--            <v-chip-->
<!--              v-for="(n, index) in activities"-->
<!--              v-bind:key="n.key"-->
<!--              :outlined="index + 1 > completedSessions"-->
<!--            >-->
<!--              {{ index + 1 }}-->
<!--            </v-chip>-->
<!--          </v-row>-->
          <v-row class="mx-5 my-3">
            <v-col>
              <div class="d-flex flex-nowrap align-center">
                <span class="mr-2 border">
                  <smart-icon
                    icon="mdi-emoticon-outline"
                    class="pa-3"
                  />
                </span>
                <span>
                  {{ intensityLabel}} {{ $t("expertTool.columnNames.intensity").toLowerCase() }}
                </span>
              </div>
            </v-col>
            <v-col>
              <div class="d-flex flex-nowrap align-center">
                <span class="mr-2 border">
                  <smart-icon icon="mdi-calendar-outline" class="pa-3" />
                </span>
                <span>
                  {{ frequencyLabel }} {{ $t("sessions") }}
                </span>
              </div>
            </v-col>
            <v-col>
              <div class="d-flex flex-nowrap align-center">
                <span class="mr-2 border">
                  <smart-icon icon="mdi-timer-outline" class="pa-3" />
                </span>
                <span>
                  {{ sessionDurationLabel }} {{ $t("minutes") }}
                </span>
              </div>
            </v-col>
            <v-col>
              <div class="d-flex flex-nowrap align-center">
                <span class="mr-2 border">
                  <smart-icon icon="mdi-dumbbell" class="pa-3" />
                </span>
                <span v-if="!strengthTraining">{{ strengthTrainingLabel || $t("sessions") }}</span>
                <span v-else>
                  {{ strengthTrainingSessions }} {{ $t("sessions") }}
                </span>
              </div>
            </v-col>
          </v-row>
          <v-row class="mx-5 my-2">
            <v-col cols="4">
              <div class="bg-color-coro-info-100" style="border-radius: 12px">
                <v-row class="ma-1 pa-1">
                  <v-col>
                    <h5>{{ $ucfirst($t("pa.physicalComplaints")) }}</h5>
                  </v-col>
                </v-row>
                <v-row v-if="physicalComplaints.length" class="ma-1 pa-1">
                  <v-col
                    v-for="c in physicalComplaints"
                    v-bind:key="c.key"
                    cols="12"
                  >
                    <p class="mb-0">{{ c.name }}</p>
                  </v-col>
                </v-row>
                <v-row v-else class="ma-1 pa-1">
                  <v-col>
                    <p class="mb-0">{{ $ucfirst($t("noData")) }}</p>
                  </v-col>
                </v-row>
              </div>
            </v-col>
            <v-col cols="4">
              <div class="bg-color-coro-info-100" style="border-radius: 12px">
                <v-row class="ma-1 pa-1">
                  <v-col>
                    <h5>{{ $ucfirst($t("pa.favoriteActivities")) }}</h5>
                  </v-col>
                </v-row>
                <v-row v-if="favoriteActivities.length" class="ma-1 pa-1">
                  <v-col
                    v-for="activity in favoriteActivities"
                    v-bind:key="activity.key"
                    cols="12"
                  >
                    <div class="v-patient-overview-pa-goal-setting-weekly-sports-goal__fav-activity">
                      <div class="border mr-3 v-patient-overview-pa-goal-setting-weekly-sports-goal__fav-activity-icon">
                        <activity-icon :activityType="activity.key" />
                      </div>
                      <div class="v-patient-overview-pa-goal-setting-weekly-sports-goal__fav-activity-label">
                        <p class="mb-0">{{ activity.name }}</p>
                        <p v-if="activity.duration" class="mb-0">{{ activity.duration }} min</p>
                      </div>
                    </div>
                  </v-col>
                </v-row>
                <v-row v-else class="ma-1 pa-1">
                  <v-col>
                     <p class="mb-0">{{ $ucfirst($t("noData")) }}</p>
                  </v-col>
                </v-row>
              </div>
            </v-col>
            <v-col cols="4">
              <div class="bg-color-coro-info-100" style="border-radius: 12px">
                <v-row class="ma-1 pa-1">
                  <v-col>
                    <h5>{{ $ucfirst($t("pa.favoriteActivitiesFromChildhood")) }}</h5>
                  </v-col>
                </v-row>
                <v-row
                  v-if="favouriteActivitiesFromChildHood.length"
                  class="ma-1 pa-1"
                >
                  <v-col
                    v-for="activity in favouriteActivitiesFromChildHood"
                    v-bind:key="activity.key"
                    cols="12"
                  >
                    <div class="v-patient-overview-pa-goal-setting-weekly-sports-goal__fav-activity">
                      <div class="border mr-3 v-patient-overview-pa-goal-setting-weekly-sports-goal__fav-activity-icon">
                        <activity-icon :activityType="activity.key" />
                      </div>
                      <div class="v-patient-overview-pa-goal-setting-weekly-sports-goal__fav-activity-label">
                        <p class="mb-0">{{ activity.name }}</p>
                        <p v-if="activity.duration" class="mb-0">{{ activity.duration }} min</p>
                      </div>
                    </div>
                  </v-col>
                </v-row>
                <v-row v-else class="ma-1 pa-1">
                  <v-col>
                    <p class="mb-0">{{ $ucfirst($t("noData")) }}</p>
                  </v-col>
                </v-row>
              </div>
            </v-col>
          </v-row>
        </v-col>

        <!-- Note: Release 2.0 scope reduction  -->
        <!-- <v-col cols="6">
          <v-row class="ma-2">
            <v-col cols="8">
              <v-row>
                <v-col
                  cols="3"
                  :style="{ alignItems: 'center', display: 'flex' }"
                >
                  <h3>{{ $ucfirst($t("pa.exercisePlan")) }}</h3>
                </v-col>
                <v-col>
                  <v-btn
                    :disabled="version == '' || version == 0 || !version"
                    v-on:click="addActivity()"
                  >
                    <smart-icon icon="mdi-plus" v-on:click="addActivity" />
                    {{ $ucfirst($t("pa.addActivity")) }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="4">
              <v-row>
                <v-col style="text-align: right">
                  <v-btn
                    :disabled="version == '' || version == 0 || !version"
                    v-on:click="clearAllActivities()"
                  >
                    <smart-icon icon="mdi-eraser" />
                    {{ $ucfirst($t("pa.clearPlan")) }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row
            class="
              mx-5
              my-2
              v-patient-overview-pa-goal-setting-weekly-sports-goal__activity-days
            "
            v-if="activityDays"
            no-gutters
          >
            <v-col
              v-for="i in 7"
              v-bind:key="i"
              class="
                v-patient-overview-pa-goal-setting-weekly-sports-goal__activity-days__day
              "
            >
              <v-row
                class="
                  v-patient-overview-pa-goal-setting-weekly-sports-goal__activity-days__day-name
                "
              >
                <strong>{{ daysToSelect[i - 1] }}</strong>
              </v-row>
              <v-row
                v-if="activityDays[i - 1] && activityDays[i - 1].activities"
                class="
                  v-patient-overview-pa-goal-setting-weekly-sports-goal__activity-days__activities
                "
              >
                <v-col
                  v-for="activity in activityDays[i - 1].activities"
                  v-bind:key="activity.id"
                  class="
                    v-patient-overview-pa-goal-setting-weekly-sports-goal__activity-days__activity
                  "
                >
                  <a @click="activityModification(activity)">
                    <div>
                      <activity-icon
                        :activityType="activity.patientActivityType.key"
                        :size="22"
                        iconColor="#303030"
                      />
                    </div>
                    <div
                      style="color: #303030; line-height: 0.9em"
                      class="mt-1"
                    >
                      {{ activity.patientActivityType.activityType.name }}
                    </div>
                    <div class="color-coro-info-500" style="font-size: 0.9em">
                      {{ activity.duration }} {{ $t("min") }}
                    </div>
                  </a>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row
            class="
              mx-5
              my-5
              v-patient-overview-pa-goal-setting-weekly-sports-goal__activity-modification
            "
            v-if="showAddActivity"
          >
            <v-col class="bg-color-coro-lightgray pa-7">
              <h3 class="mb-2">{{ $ucfirst($t("pa.addActivity")) }}</h3>
              <v-row>
                <v-col>
                  <v-row
                    ><p>{{ $ucfirst($t("day")) }}</p></v-row
                  >
                  <v-row>
                    <v-select
                      :items="daysToSelect"
                      :value="$ucfirst($t('date.monday'))"
                      v-on:change="(e) => onChange(e, 'day')"
                    />
                  </v-row>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-row
                    ><p>{{ $ucfirst($t("pa.activity")) }}</p></v-row
                  >
                  <v-row>
                    <v-autocomplete
                      :items="activitiesToSelect"
                      value="running"
                      v-on:change="(e) => onChange(e, 'activityType')"
                    />
                  </v-row>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-row
                    ><p>{{ $ucfirst($t("expertTool.columnNames.intensity")) }}</p></v-row
                  >
                  <v-row>{{ textForIntensity }}</v-row>
                  <v-row>
                    <v-slider
                      min="1"
                      max="3"
                      v-on:change="(e) => onChange(e, 'intensity')"
                      thumb-label="always"
                      :thumb-size="24"
                      ticks="always"
                      tick-size="6"
                      :value="tempActivity.intensity"
                    />
                  </v-row>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-row>
                    <v-col cols="12">
                      <v-row>
                        <p>{{ $ucfirst($t("pa.duration")) }}</p>
                      </v-row>
                      <v-row v-if="sessionDurationLabel">
                        <small>{{ sessionDurationLabel }} {{ $t("minutes") }}</small>
                      </v-row>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-text-field
                      v-on:change="(e) => onChange(e, 'duration')"
                      type="number"
                      min="0"
                      step="1"
                      :rules="numberRules"
                    >
                      <p slot="append">{{ $ucfirst($t("min")) }}</p>
                    </v-text-field>
                  </v-row>
                </v-col>
              </v-row>
              <v-row>
                <v-spacer></v-spacer>
                <v-btn @click="cancelAcitivityModification" class="mr-2">
                  <smart-icon icon="mdi-cancel" size="16" />
                  {{ $ucfirst($t('buttons.cancel', {catalog: 'app_mobile'})) }}
                </v-btn>
                <v-btn @click="saveActivity" :disabled="!valid">
                  <smart-icon icon="mdi-plus" size="16" />
                  {{ $ucfirst($t("add")) }}
                </v-btn>
              </v-row>
            </v-col>
          </v-row>
          <v-row
            class="
              mx-5
              my-5
              v-patient-overview-pa-goal-setting-weekly-sports-goal__activity-modification
            "
            v-if="selectedActivityForModification"
          >
            <v-col class="bg-color-coro-lightgray pa-7">
              <h3 class="mb-2">{{ $ucfirst($t("pa.editActivity")) }}</h3>
              <v-row class="">
                <v-col>
                  <v-row
                    ><p>{{ $ucfirst($t("day")) }}</p></v-row
                  >
                  <v-row>
                    <v-select
                      :items="daysToSelect"
                      :value="
                        selectedActivityForModification.date | dayNameFull
                      "
                      v-on:change="(e) => onChange(e, 'day')"
                      :rules="requiredRules"
                    />
                  </v-row>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-row
                    ><p>{{ $ucfirst($t("pa.activity")) }}</p></v-row
                  >
                  <v-row>
                    <v-autocomplete
                      :items="activitiesToSelect"
                      :value="
                        selectedActivityForModification.patientActivityType.key
                      "
                      v-on:change="(e) => onChange(e, 'activityType')"
                      :rules="requiredRules"
                      required
                    />
                  </v-row>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-row
                    ><p>{{ $ucfirst($t("expertTool.columnNames.intensity")) }}</p></v-row
                  >
                  <v-row
                    ><p>{{ textForIntensity }}</p></v-row
                  >
                  <v-row>
                    <v-slider
                      min="1"
                      max="3"
                      :value="selectedActivityForModification.intensity"
                      v-on:change="(e) => onChange(e, 'intensity')"
                      thumb-label="always"
                      :thumb-size="24"
                      ticks="always"
                      tick-size="6"
                    />
                  </v-row>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-row
                    ><p>{{ $ucfirst($t("pa.duration")) }}</p></v-row
                  >
                  <v-row>
                    <v-text-field
                      :value="selectedActivityForModification.duration"
                      v-on:change="(e) => onChange(e, 'duration')"
                      type="number"
                    >
                      <p slot="append">{{ $ucfirst($t("min")) }}</p>
                    </v-text-field>
                  </v-row>
                </v-col>
              </v-row>
              <v-row>
                <v-spacer></v-spacer>
                <v-btn @click="cancelAcitivityModification" class="mr-2">
                  <smart-icon icon="mdi-cancel" size="16" />
                  {{ $ucfirst($t('buttons.cancel', {catalog: 'app_mobile'})) }}
                </v-btn>
                <v-btn
                  @click="deleteActivity(selectedActivityForModification)"
                  class="mr-2"
                >
                  <smart-icon icon="mdi-delete-outline" size="16" />
                  {{ $ucfirst($t("delete")) }}
                </v-btn>
                <v-btn @click="saveActivity">
                  <smart-icon icon="mdi-content-save-outline" size="16" />
                  {{ $ucfirst($t('buttons.save', {catalog: 'app_mobile'})) }}
                </v-btn>
              </v-row>
            </v-col>
          </v-row>
        </v-col> -->
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import PAWeeklySportsGoal from "../../../components/Parts/PAWeeklySportsGoal";
import SmartIcon from "../../../components/Parts/SmartIcon.vue";
import ActivityIcon from "../../../components/Parts/ActivityIcon.vue";
import moment from "moment";
import gql from "graphql-tag";
import { mapGetters } from "vuex";

import { ROLE_EXPERT_TOOL } from "dashboard/store/user/role-tree";

/**
 * Component PatientOverviewPAGoalSettingWeeklySportsGoal.vue
 *
 * @module PatientOverviewPAGoalSettingWeeklySportsGoal
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.7] app/src/APP_DASHBOARD/templates/views/Patient/PA/PatientOverviewPAGoalSettingWeeklySportsGoal.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: "PatientOverviewPAGoalSettingWeeklySportsGoal",
  components: {
    PAWeeklySportsGoal,
    SmartIcon,
    ActivityIcon,
  },
  filters: {
    dayName: function (date) {
      return date ? moment(date).format("ddd") : "";
    },
    dayNameFull: function (date) {
      return date ? moment(date).format("dddd") : "";
    },
  },
  data: function () {
    return {
      valid: true,
      activities: [],
      flag: 0,
      finish: 0,
      progress: 0,
      activitiesPlanned: 5,
      activitiesPerformed: 0,
      minSessions: 0,
      maxSessions: 0,
      minSessionDuration: 0,
      maxSessionDuration: 0,
      feelScale: 0,
      sessionDurationLabel: null,
      frequencyLabel: null,
      minIntensityLabel: null,
      intensityLabel: null,
      programDurationLabel: null,
      completedSessions: 0,
      activityDays: null,
      startDate: null,
      daysToSelect: [
        this.$ucfirst(this.$t("date.monday")),
        this.$ucfirst(this.$t("date.tuesday")),
        this.$ucfirst(this.$t("date.wednesday")),
        this.$ucfirst(this.$t("date.thursday")),
        this.$ucfirst(this.$t("date.friday")),
        this.$ucfirst(this.$t("date.saturday")),
        this.$ucfirst(this.$t("date.sunday")),
      ],
      textsForIntensity: [
        this.$ucfirst(this.$t("pa.leastIntensive")),
        this.$ucfirst(this.$t("pa.moderateIntensive")),
        this.$ucfirst(this.$t("pa.ExhaustiveIntensive")),
      ],
      textForIntensity: this.$ucfirst(this.$t("pa.leastIntensive")),
      activitiesToSelect: [],
      selectedActivityForModification: null,
      tempActivity: {
        intensity: null,
        activityType: null,
        duration: null,
        date: null,
        id: null,
      },
      showAddActivity: false,
      weekId: null,
      favoriteActivities: [],
      favouriteActivitiesFromChildHood: [],
      physicalComplaints: [],
      requiredRules: [
        (v) => !!v || this.$ucfirst(this.$t("validation.IS_REQUIRED")),
      ],
      numberRules: [
        (v) => {
          if ((v || "").toString().includes("e")) {
            return this.$ucfirst(this.$t("validation.onlyNumbersAreAllowed"));
          }
          if (!v) return this.$ucfirst(this.$t("validation.IS_REQUIRED"));
          if (v < 0)
            return this.$ucfirst(this.$t("validation.onlyPositiveNumbers"));
        },
      ],
      showDialog: false,
      patientStudyId: '',
      studyIdError: '',
      strengthTraining: false,
      strengthTrainingLabel: null,
      strengthTrainingSessions: 0
    };
  },
  computed: {
    ...mapGetters({
      patientId: "patientSummary/id",
      studyId: "patientSummary/studyId",
      version: "encounter/version",
    }),
    isExpertToolAvailable() {
      return this.$store.getters["user/isGranted"](ROLE_EXPERT_TOOL);
    },
  },
  watch: {
    patientId: function () {
      this.getActivityWeek();
      this.getFavouriteActivities();
      this.getPhysicalComplaints();
      this.getFavouriteActivitiesFromChildHood();
      // Note: Release 2.0 scope reduction
      // this.getActivityTypes();
    },
    version: function() {
      console.log('VERSION', this.version)
    },
    showDialog(curr) {
      if (curr) {
        setTimeout(() => {
          this.$refs.dialogInput.focus();
        }, 200)
      }
    }
  },
  mounted: function () {
    this.setStartDate();
    this.getActivityWeek();
    this.getFavouriteActivities();
    this.getPhysicalComplaints();
    this.getFavouriteActivitiesFromChildHood();
    // Note: Release 2.0 scope reduction
    // this.getActivityTypes();
  },
  methods: {
    setStartDate() {
      this.startDate = moment()
        .clone()
        .add(1, "weeks")
        .startOf("isoWeek")
        .format("YYYY-MM-DD")
    },

    addActivity() {
      console.log("ADD", this.startDate);

      // Set default for tempActivity:
      this.tempActivity = {
        intensity: 2,
        activityType: "running",
        duration: null,
        date: `${moment(this.startDate).format("YYYY-MM-DD")} 00:00:00`,
      };
      this.selectedActivityForModification = null;
      this.showAddActivity = true;
      this.textForIntensity = this.textsForIntensity[1];
    },

    activityModification(activity) {
      console.log("MOD", activity);
      this.selectedActivityForModification = activity;
      this.textForIntensity = this.textsForIntensity[activity.intensity - 1];

      this.tempActivity = {
        date: activity.date,
        intensity: activity.intensity,
        duration: activity.duration,
        activityType: activity.patientActivityType.key,
        id: activity.id,
      };
    },

    cancelAcitivityModification() {
      if (confirm(this.$ucfirst(this.$t("pa.areYouSureYouWantToCancel")))) {
        this.selectedActivityForModification = null;
        this.showAddActivity = false;

        this.tempActivity = {
          intensity: null,
          activityType: null,
          duration: null,
          date: null,
          id: null,
        };
      }
    },

    onChange(newValue, type) {
      switch (type) {
        case "day": {
          let startDate = moment(this.startDate);

          let index = this.daysToSelect.findIndex((val) => val == newValue);
          let newDay = startDate
            .clone()
            .isoWeekday(index + 1)
            .format("YYYY-MM-DD HH:mm:ss");

          console.log(newValue, type, newDay);
          this.tempActivity = {
            ...this.tempActivity,
            date: newDay,
          };

          break;
        }
        case "activityType":
          console.log(newValue, type);
          this.tempActivity = {
            ...this.tempActivity,
            activityType: newValue,
          };
          break;
        case "intensity": {
          console.log(newValue, type);

          this.textForIntensity = this.textsForIntensity[newValue - 1];

          this.tempActivity = {
            ...this.tempActivity,
            intensity: newValue,
          };
          break;
        }
        case "duration":
          this.tempActivity = {
            ...this.tempActivity,
            duration: newValue,
          };
          break;
      }

      console.log(this.tempActivity);
    },

    deleteActivity(activity) {
      console.log("TRYING TO DELETE:", activity.id, activity);
      if (confirm(this.$ucfirst(this.$t("pa.areYouSureYouWantToDelete", {
        params: {
          duration: activity.duration + " min",
          activity: activity.patientActivityType.activityType.name
        }
      })))) {

        this.$app.apolloMobileClient
          .mutate({
            mutation: gql`
              mutation deleteMyActivity($id: String!, $patientId: Int) {
                deleteMyActivity(id: $id, patientId: $patientId)
              }
            `,
            variables: {
              id: activity.id,
              patientId: this.patientId,
            },
          })
          .then((result) => {
            // Updated!
            console.log("DELETED: ", activity.id, result);
            // AFTER DELETE:
            this.selectedActivityForModification = null;
            this.getActivityWeek();
          })
          .catch((err) => {
            // this.$toasted.show(this.$ucfirst(this.$t('login.loggedout')))
            console.log(err);
            this.$toasted.show("Cannot do mutation. Try again later.");
          });
      }
    },

    clearAllActivities() {
      if (confirm(this.$ucfirst(this.$t("pa.areYouSureYouWantToClearPlan")))) {
        this.$app.apolloMobileClient
          .mutate({
            mutation: gql`
              mutation clearMyActivitiesForWeek(
                $patientId: Int!
                $weekId: String!
              ) {
                clearMyActivitiesForWeek(patientId: $patientId, id: $weekId) {
                  id
                }
              }
            `,
            variables: {
              patientId: parseInt(this.patientId),
              weekId: this.weekId,
            },
          })
          .then((result) => {
            console.log("DELETED ALL ACTIVITIES", result);

            this.getActivityWeek();
          });
      }
    },

    saveActivity() {
      console.log("READY TO SAVE.", this.tempActivity);
      if (this.$refs.form.validate()) {
        this.$app.apolloMobileClient
          .mutate({
            mutation: gql`
              mutation updateActivity(
                $patientId: Int!
                $duration: Int!
                $intensity: Int!
                $date: DateTime!
                $activityKey: String!
                $id: String
              ) {
                createUpdateMyActivity(
                  activity: {
                    activityKey: $activityKey
                    id: $id
                    duration: $duration
                    intensity: $intensity
                    date: $date
                    patientId: $patientId
                  }
                ) {
                  id
                  duration
                  intensity
                  achievability
                  time
                  date
                  status
                  completed
                  patientActivityType {
                    id
                    key
                    favourite
                    recommended
                    activityType {
                      id
                      key
                      category {
                        id
                        key
                        name
                        type
                      }
                    }
                  }
                }
              }
            `,
            variables: {
              patientId: parseInt(this.patientId),
              duration: parseInt(this.tempActivity.duration),
              intensity: parseInt(this.tempActivity.intensity),
              date: this.tempActivity.date
                ? this.tempActivity.date
                : this.startDate,
              activityKey: this.tempActivity.activityType,
              id: this.tempActivity.id,
            },
          })
          .then((result) => {
            console.log("SAVED", result);

            this.getActivityWeek();

            // RESET FORM.
            this.selectedActivityForModification = null;

            this.tempActivity = {
              intensity: null,
              activityType: null,
              duration: null,
              date: null,
              id: null,
            };

            // Show toast.
            this.$toasted.show(this.$ucfirst(this.$t("pa.activityUpdated")));

            // hide AddActivity
            this.showAddActivity = false;
          });
      }
    },

    getActivityTypes() {
      this.$app.apolloMobileClient
        .query({
          query: gql`
            {
              activityTypes {
                edges {
                  node {
                    id
                    key
                    name
                  }
                }
              }
            }
          `,
        })
        .then((result) => {
          result.data.activityTypes.edges.forEach((edge) => {
            if (edge.node.key != "6mwt") {
              this.activitiesToSelect.push({
                value: edge.node.key,
                text: edge.node.name,
              });
            }
          });
        });
    },

    getPhysicalComplaints() {
      this.$app.apolloMobileClient
        .query({
          query: gql`
            query myPhysicalComplaints($patientId: String!) {
              myQuestionnaireForms(
                conditions: {
                  gate: and
                  inputs: [
                    { field: key, value: "PHYSICALCOMPLAINTS" }
                  ]
                }
                orders: { field: version, direction: desc }
                limit: 1
                functions: { function: patientId, parameters: [$patientId] }
              ) {
                questionnaire {
                  key
                }

                id
                completed
                score
                entryDate
                entries {
                  id
                  question {
                    key
                    text
                    sequence
                  }
                  givenAnswers {
                    id
                    key
                    score
                    text
                    openAnswer
                    openAnswerObject
                  }
                }
              }
            }
          `,
          variables: {
            patientId: this.patientId,
          },
        })
        .then((result) => {
          let tempAnswers = [];
          const nestedKeys = ["OTHER_JOINT", "SHOULDER", "KNEE", "HIP", "BACK", "NECK"];
          const root = "MUSCLE_JOINT"
          const other = "OTHER"

          const hasNested = result.data.myQuestionnaireForms[0]?.entries[0].givenAnswers.some(answer => nestedKeys.includes(answer.key))
          const hasOther = result.data.myQuestionnaireForms[0]?.entries[1].givenAnswers.some(answer => !answer.key && answer.openAnswer)

          result.data.myQuestionnaireForms[0]?.entries.forEach((entry) => {
            // REGULAR QUESTIONS.
            entry.givenAnswers.forEach((answer) => {
              const exclude = (answer.key === root && hasNested) || (answer.key === other && hasOther)

              if (!exclude) {
                tempAnswers.push({
                  key: answer.key,
                  name: answer.openAnswer ? answer.openAnswer : answer.text,
                });
              }
            });
          });

          this.physicalComplaints = tempAnswers;
        });
    },

    getFavouriteActivitiesFromChildHood() {
      // SPORTPREFERENCESKIDS
      this.$app.apolloMobileClient
        .query({
          query: gql`
            query myPhysicalComplaints($patientId: String!) {
              myQuestionnaireForms(
                conditions: {
                  gate: and
                  inputs: [
                    { field: key, value: "SPORTPREFERENCESKIDS" }
                  ]
                }
                orders: { field: version, direction: desc }
                limit: 1
                functions: { function: patientId, parameters: [$patientId] }
              ) {
                questionnaire {
                  key
                }

                id
                completed
                score
                entryDate
                entries {
                  id
                  question {
                    key
                    text
                    sequence
                  }
                  givenAnswers {
                    id
                    key
                    score
                    text
                    openAnswer
                    openAnswerObject
                  }
                }
              }
            }
          `,
          variables: {
            patientId: this.patientId,
          },
        })
        .then((result) => {
          let tempAnswers = [];

          // console.log('FORM', result.data.myQuestionnaireForms);

          result.data.myQuestionnaireForms[0]?.entries.forEach(
            (entry, index) => {
              // REGULAR QUESTIONS.
              entry.givenAnswers.forEach((answer) => {
                if (answer.openAnswerObject && answer.openAnswerObject != "") {
                  let activities = JSON.parse(answer.openAnswerObject);

                  activities.forEach((act) => {
                    tempAnswers.push({
                      key: act.toUpperCase(),
                      name: `${this.$ucfirst(this.$t("medicationRejectionOfGuideline.other"))} ${act}`
                    });
                  });
                } else if (answer.key) {
                  tempAnswers.push({
                    key: answer.key,
                    name: answer.text,
                  });
                }
              });
            }
          );

          console.log(tempAnswers);

          this.favouriteActivitiesFromChildHood = tempAnswers;
        });
    },

    getFavouriteActivities() {
      this.favoriteActivities = [];

      this.$app.apolloMobileClient
        .query({
          query: gql`
            query favouriteActivities($patientId: Int) {
                myFavouriteActivities(patientId : $patientId){
                  key
                  name
                  source
                  duration
                }
              }
          `,
          variables: {
            patientId: parseInt(this.patientId),
          },
        })
        .then((result) => {
          result.data.myFavouriteActivities.forEach((n) => {
            this.favoriteActivities.push({
              key: n.key,
              name: n.name,
              duration: n.duration
            });
          });
        });
    },

    getActivityWeek() {
      this.$app.apolloMobileClient
        .query({
          query: gql`
            query myActivityWeeks(
              $patientId: String!
              $from: String!
              $until: String!
            ) {
              myActivityWeeks(
                functions: [
                  { function: patientId, parameters: [$patientId] }
                  { function: excludeDeletedActivities }
                ]
                orders: [
                  { field: weekStartDate, direction: desc }
                  { field: activityDate, direction: asc }
                ]
                conditions: {
                  field: activityDate
                  operator: between
                  parameters: [$from, $until]
                }
                limit: 1
              ) {
                edges {
                  node {
                    id
                    caloriesBurned
                    caloriesPlanned
                    caloriesPlannedFromWalking
                    caloriesPlannedFromActivities
                    caloriesBurnedFromWalking
                    caloriesBurnedFromActivities
                    minimalCalories
                    maximalCalories
                    minimalIntensity
                    maximalIntensity
                    minimalSessionDuration
                    maximalSessionDuration
                    intensityStrengthTraining
                    daysStrengthTraining
                    daysStrengthTrainingGoal
                    startDate
                    completedActivityCount
                    activityCount
                    planning {
                      minSessions
                      maxSessions
                      minDuration
                      maxDuration
                      feelScale
                      strengthTraining
                      minSessionDuration
                      maxSessionDuration
                      frequencyLabel
                      strengthTrainingLabel
                      minIntensityLabel
                      intensityLabel(includeFlagText : false)
                      sessionDurationLabel(includeFlagText : false)
                      programDurationLabel(includeFlagText : false)
                    }
                    # activityDays {
                    #   id
                    #   date
                    #   activities {
                    #     id
                    #     date
                    #     time
                    #     intensity
                    #     duration
                    #     patientActivityType {
                    #       key
                    #       activityType {
                    #         name
                    #       }
                    #     }
                    #   }
                    # }
                  }
                }
              }
            }
          `,
          variables: {
            patientId: this.patientId,
            from: moment()
              .clone()
              .add(1, "weeks")
              .startOf("isoWeek")
              .format("YYYY-MM-DD"),
            until: moment()
              .clone()
              .add(1, "weeks")
              .endOf("isoWeek")
              .format("YYYY-MM-DD"),
          },
        })
        .then((result) => {
          console.log("ACTIVITYWEEK", result);
          const activityWeek = result.data.myActivityWeeks?.edges[0]?.node;

          this.flag = activityWeek?.minimalCalories || 0;
          this.finish = activityWeek?.maximalCalories || 0;
          this.progress = activityWeek?.caloriesPlannedFromActivities || 0;

          this.activitiesPerformed = activityWeek?.completedActivityCount || 0;
          this.activitiesPlanned = activityWeek?.activityCount || 5;

          this.minSessions = activityWeek?.planning.minSessions || 0;
          this.maxSessions = activityWeek?.planning.maxSessions || 0;
          this.sessionDurationLabel = activityWeek?.planning.sessionDurationLabel || null;
          this.frequencyLabel = activityWeek?.planning.frequencyLabel || null;
          this.minIntensityLabel = activityWeek?.planning.minIntensityLabel || null;
          this.intensityLabel = activityWeek?.planning.intensityLabel || null;
          this.programDurationLabel = activityWeek?.planning.programDurationLabel || null;
          this.minSessionDuration = activityWeek?.planning.minSessionDuration || 0;
          this.maxSessionDuration = activityWeek?.planning.maxSessionDuration || 0;
          this.feelScale = activityWeek?.planning.feelScale || 0;
          this.strengthTraining = activityWeek?.planning.strengthTraining || false;
          this.strengthTrainingLabel = activityWeek?.planning.strengthTrainingLabel || null;
          this.strengthTrainingSessions = activityWeek?.daysStrengthTrainingGoal || 0;

          this.activityDays = activityWeek?.activityDays || null;
          this.startDate = activityWeek?.startDate || moment()
              .clone()
              .add(1, "weeks")
              .startOf("isoWeek")
              .format("YYYY-MM-DD");

          this.weekId = activityWeek?.id || null;
          // // Note: Release 2.0 scope reduction
        //   // RESET.
        //   this.activities = [];
        //
        //   // Calculate number of completed sessions.
        //   let completedSessions = 0;
        //   let minimumSessionDuration = activityWeek?.planning?.minSessionDuration || null;
        //
        //   if (minimumSessionDuration !== null && activityWeek?.activityDays) {
        //     console.log('MIN SES DUR', minimumSessionDuration);
        //
        //     activityWeek?.activityDays.forEach((activityDay) => {
        //       if (activityDay.activities.length) {
        //         let activitiesWithoutSessionDurationForDay = 0;
        //         let activitiesWithoutSessionForDay = [];
        //
        //         activityDay.activities.forEach((activity) => {
        //           this.activities.push(activity);
        //
        //           // if the activity duration is at least the minimal session duration, it counts as a completed session
        //           if(activity.duration >= minimumSessionDuration) {
        //             completedSessions++;
        //           } else {
        //             activitiesWithoutSessionDurationForDay += activity.duration;
        //             activitiesWithoutSessionForDay.push(activity);
        //           }
        //         });
        //
        //         console.log('TOTAL DUR for activities without sessions', activitiesWithoutSessionDurationForDay);
        //
        //         // if the activity duration is not at least the minimal session duration, it should be checked if there
        //         // were any other activities on the same day that did not count as a session. If this is the case,
        //         // the duration of these smaller activities should be accumulated and checked if this reaches the
        //         // minimal session duration. If the accumulated duration is at least the minimal session duration,
        //         // it also counts as a completed session.
        //         if(activitiesWithoutSessionDurationForDay >= minimumSessionDuration) {
        //           completedSessions++;
        //         }
        //       }
        //     });
        //   }
        //
        //   console.log('Completed sessions', completedSessions, minimumSessionDuration);
        //   this.completedSessions = completedSessions;
        });
    },

    editExpertTool() {
      if (this.showDialog === true) {
        if (!this.patientStudyId) {
          this.studyIdError = this.$ucfirst(this.$t("validation.IS_REQUIRED"))
        } else if (this.patientStudyId.toLowerCase() !== this.studyId.toLowerCase()) {
          this.studyIdError = '';
          this.$toasted.show(
            this.$ucfirst(this.$t('validation.subjectIdDoesntCorrespond')) + ' ' +
            this.$ucfirst(this.$t('validation.enterCorrectIdToOpenExpertTool')), {
              type : 'error',
              position: 'bottom-center',
              duration : 6000
          });
        } else {
          this.$router.push({ name: 'PatientExpertToolSportsGoal', patientId: this.patientId });
          this.closeDialog();
        }
      } else {
        this.showDialog = true
      }
    },

    closeDialog() {
      this.showDialog = false;
      this.studyIdError = '';
      this.patientStudyId = '';
    }
  },

  // Note: Release 2.0 scope reduction
  // beforeRouteLeave (to, from , next) {
  //   // Check for ongoing SDM Encounter.
  //   if(this.version == '' || this.version == 0 || !this.version) {
  //     next();
  //   } else {
  //     // Check for number of sessions.
  //     if(this.activities.length < parseInt(this.minSessions)) {
  //       console.log(`only ${this.completedSessions}/${this.minSessions} completed sessions`)
  //       const answer = window.confirm(
  //         this.$ucfirst(this.$t(
  //           "startMoving.alerts.notEnoughSessions",
  //           { params: { minimumNumberOfSessions: this.minSessions }, catalog: 'app_mobile' }
  //         ))
  //       )
  //       if (answer) {
  //         next(false)
  //       } else {
  //         next()
  //       }
  //     } else {
  //       next();
  //     }
  //   }
  // },
};
</script>

<style lang="scss">
.v-chip {
  margin-right: 8px;
}

.v-patient-overview-pa-goal-setting-weekly-sports-goal__activity-days {
  overflow: hidden;

  .v-patient-overview-pa-goal-setting-weekly-sports-goal__activity-days__day {
    border: 1px solid #ccc;
  }

  .v-patient-overview-pa-goal-setting-weekly-sports-goal__activity-days__day-name {
    display: flex;
    align-items: center;
    justify-content: center;
    border-bottom: 1px solid #ccc;
    padding: 8px 0;
  }

  .v-patient-overview-pa-goal-setting-weekly-sports-goal__activity-days__activities {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 120px;
  }

  .v-patient-overview-pa-goal-setting-weekly-sports-goal__activity-days__activity {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .v-patient-overview-pa-goal-setting-weekly-sports-goal__activity-days__activity
    > a {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
  }
}

.v-patient-overview-pa-goal-setting-weekly-sports-goal__fav-activity {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;

  & .v-patient-overview-pa-goal-setting-weekly-sports-goal__fav-activity-icon {
    min-width: 45px;
    width: 45px;
    height: 45px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  & .v-patient-overview-pa-goal-setting-weekly-sports-goal__fav-activity-label {
    word-break: break-word;
  }
}

</style>
